import AlertPopup from "components/alert-popup/AlertPopup";
import Button from "core/buttons/button/Button";
import DefinitiveBackupForm from "components/form/mission/DefinitiveBackupForm";
import ValidationModal from "components/modal/ValidationModal";
import { DEFINITIVE_BACKUP_DEFAULT_VALUES } from "constants/forms";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { FILTERS_FOR_NAMES, POPUP_STATUS, QUERY_STATUS } from "constants/constants";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMissionContext } from "api/mission/MissionProvider";
import { useParams, useNavigate } from "react-router-dom";
import { useUserContext } from "api/user/UserProvider";
import { useMissionBackupContext } from "api/mission/mission-backup/MissionBackupProvider";
import { useGeneralContext } from "api/directory/general/GeneralProvider";

const DefinitiveBackup = () => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      ...DEFINITIVE_BACKUP_DEFAULT_VALUES,
      manager_id: [],
    },
  });

  const { slug } = useParams();
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [managerValue, setManagerValue] = useState([]);
  const navigate = useNavigate();
  const replacementStartDate = watch("replacement_start_date");
  const { getMissionBySlug, missionBySlug, editMissionById, missionCreated } =
    useMissionContext();
  const { lang, userScope, apiErrorResponse, setApiErrorResponse } = useUserContext();
  const {
    createMissionBackup,
    missionBackupCreatedStatus,
    setMissionBackupCreatedStatus,
  } = useMissionBackupContext();
  const { getNamesByFilters, namesByFilters } = useGeneralContext();

  useEffect(() => {
    setApiErrorResponse(null);
    getMissionBySlug({ slug });
    return () => setApiErrorResponse(null);
  }, []);

  const resolveManager = useCallback(async () => {
    if (!missionBySlug?.manager_id) return;
    const result = await getNamesByFilters({
      id: [missionBySlug.manager_id],
      types: [FILTERS_FOR_NAMES.PERSON],
    });
    if (result?.[0]) {
      const mgr = result[0];
      const formatted = [
        { value: mgr.id, label: `${mgr.first_name} ${mgr.last_name}` },
      ];
      setManagerValue(formatted);
      setValue("manager_id", formatted);
    }
  }, [missionBySlug?.manager_id]);

  useEffect(() => {
    resolveManager();
  }, [resolveManager]);

  const onSubmit = (data) => {
    setIsModalOpen(true);
    createMissionBackup({
      data: {
        worker_id: data?.worker_id[0]?.value,
        original_mission_id: missionBySlug?.id,
        original_end_date: data?.replacement_start_date,
        backup_start_date: data?.arrival_date_replacement,
      },
    });
  };

  useEffect(() => {
    if (missionCreated?.id) {
      editMissionById({
        id: missionBySlug?.id,
        data: {
          ...missionBySlug,
          end_date: replacementStartDate,
          backup_of_id: missionCreated?.id,
        },
      });
    }
  }, [missionCreated]);

  const onError = () => {
    setCellHighlight(true);
    setStatus(POPUP_STATUS.ERROR);
    setCode(FIELD_REQUIRED);
  };


  return (
    <>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <ValidationModal
        open={isModalOpen}
        status={missionBackupCreatedStatus}
        onClose={() => {
          setIsModalOpen(false);
          if (missionBackupCreatedStatus === QUERY_STATUS.SUCCESS) {
            navigate(`/${userScope}/missions/${slug}`);
          }
          setMissionBackupCreatedStatus(QUERY_STATUS.INITIAL);
        }}
        messages={{
          pendingTitleId: "modal_validation_create_file",
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: "modal_validation_create_file",
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: "modal_validation_create_file",
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
      />
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        style={{ paddingTop: "2rem" }}
      >
        <DefinitiveBackupForm
          apiErrorResponse={apiErrorResponse}
          cellHighlight={cellHighlight}
          control={control}
          errors={errors}
          getNamesByFilters={getNamesByFilters}
          namesByFilters={namesByFilters}
          lang={lang}
          managerValue={managerValue}
          register={register}
          setValue={(field, value) => {
            setValue(field, value);
            clearErrors(field);
          }}
          clearErrors={clearErrors}
          watch={watch}
        />
        <div className="validate-section">
          <Button color="btn-medium gray-700" id="button-form-validation">
            <FormattedMessage id="validate" defaultMessage="Valider" />
          </Button>
        </div>
      </form>
    </>
  );
};

export default DefinitiveBackup;
